import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { SelectItemSearch } from '@/core/SelectItemSearch.molecule';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import { getFormFieldProps, getValidationFunction } from '@/formbuilder/formbuilder.utilities';
import { SeeqNames } from '@seeqdev/constants';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import type { SearchModes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';
import type { IconType } from '@seeqdev/qomponents';

export interface SelectItemSearchFormComponentProps extends ValidatingFormComponent<SelectedSearchItem[]> {
  component: 'SelectItemSearchFormComponent';
  iconPartialTooltipKey: string;
  scopeIds?: string[];
  excludeGloballyScoped?: boolean;
  helpTextKey?: string;
  customErrorText?: string;
  title: string;
  notSelectedMessage: string;
  searchTypes?: string[];
  /** determines if the component will handle single or multiple item selection in the modal*/
  isMultiple?: boolean;
  showAddIcon?: boolean;
  canSelectAll?: boolean;
  showPinnedAssets?: boolean;
  disableInput?: boolean;
  showOnlyConditions?: boolean;
  showOnlyScopedAssets?: boolean;
  showModal?: boolean;
  setShowModal?: (value: boolean) => void;
  validateItem?: (item: SelectedSearchItem) => Promise<boolean>;
  filterByWorkbook?: boolean;
  showRecentlyAccessed?: boolean;
  overrideWorkbookId?: string;
  itemTypeToShowInOverview?: SearchModes;
  hideAddEditIcon?: boolean;
  hideDeleteIcon?: boolean;
  deleteIconStyle?: IconType;
  overviewLabel?: string;
  showCreateNewItem?: boolean;
  onCreateNewItem?: () => void;
  createNewItemMessage?: string;
  createNewItemLabel?: string;
  label?: string;
  canBeEdited?: boolean;
  restrictExploration?: boolean;
  initialItemTable?: SelectedSearchItem;
  initialHierarchy?: SelectedSearchItem;
  initialScalingTable?: SelectedSearchItem;
}

const DEFAULT_SEARCH_TYPES = [SeeqNames.Types.Asset];

export const defaultItemValidation = () => Promise.resolve(true);

export const SelectItemSearchFormComponent = (props: SelectItemSearchFormComponentProps) => {
  const {
    name,
    //currently selected items in the form after save is clicked in the modal
    value,
    testId = 'selectAsset',
    validation,
    extendValidation,
    iconPartialTooltipKey,
    helpTextKey,
    customErrorText,
    scopeIds,
    excludeGloballyScoped = false,
    title,
    notSelectedMessage,
    searchTypes = DEFAULT_SEARCH_TYPES,
    isMultiple = false,
    showAddIcon = false,
    required = true,
    canSelectAll = false,
    showPinnedAssets = false,
    disableInput = false,
    showOnlyConditions = false,
    showOnlyScopedAssets = false,
    showModal = false,
    setShowModal = _.noop,
    validateItem = defaultItemValidation,
    filterByWorkbook,
    showRecentlyAccessed = false,
    overrideWorkbookId,
    itemTypeToShowInOverview,
    hideAddEditIcon = false,
    hideDeleteIcon = false,
    deleteIconStyle,
    overviewLabel,
    showCreateNewItem = false,
    onCreateNewItem,
    createNewItemMessage = '',
    createNewItemLabel = '',
    label,
    canBeEdited = true,
    restrictExploration = false,
    initialItemTable,
    initialHierarchy,
    initialScalingTable,
  } = props;
  const { t } = useTranslation();

  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    (_.has(formState.dirtyFields, name) || _.has(formState.dirtyFieldsSinceLastSubmit, name)) &&
    formState.hasValidationErrors;

  const defaultValidation = (value: SelectedSearchItem[]) => required && !value;
  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  return (
    <FormFieldWrapper
      wrapperClassNames="flexFill"
      testId={testId}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation} value={value}>
        {({ input, meta }) => {
          const { onChange, value, extraClassNames } = getFormFieldProps(formState, input, meta, props);

          return (
            <SelectItemSearch
              {...getFormFieldProps(formState, input, meta, props)}
              testId={testId}
              value={value}
              onChange={onChange}
              isMultiple={isMultiple}
              notSelectedMessage={notSelectedMessage}
              showModal={showModal}
              onModalClose={() => setShowModal(false)}
              showAddIcon={showAddIcon}
              isDisabled={disableInput}
              extraValueClassNames={extraClassNames}
              hideAddEditIcon={hideAddEditIcon}
              hideDeleteIcon={hideDeleteIcon}
              deleteIconStyle={deleteIconStyle}
              label={label}
              canBeEdited={canBeEdited}
              restrictExploration={restrictExploration}
              initialItemTable={initialItemTable}
              initialHierarchy={initialHierarchy}
              initialScalingTable={initialScalingTable}
              modalProps={{
                selectionType: 'Other',
                iconPartialTooltipKey,
                scopeIds,
                excludeGloballyScoped,
                searchTypes,
                showPinnedAssets,
                showOnlyConditions,
                showOnlyScopedAssets,
                validateItem,
                header: { title: t(title) },
                canSelectAll,
                helpTextKey,
                filterByWorkbook,
                showRecentlyAccessed,
                overrideWorkbookId,
                itemTypeToShowInOverview,
                overviewLabel,
                showCreateNewItem,
                onCreateNewItem,
                createNewItemMessage,
                createNewItemLabel,
              }}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
